import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import toNumber from 'lodash/toNumber';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import LoadingButton from '@atlaskit/button/loading-button';
import { ErrorMessage, Field, FormSection } from '@atlaskit/form';
import Select, { OptionProps, ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import AddIcon from '@atlaskit/icon/glyph/add';
import Button from '@atlaskit/button';
import { BenefitCustomisableValue } from '@css-xp/graphtypes';
import { Maybe, Promotion } from '@css-xp/graphtypes/src/generated-types-new';
import { PromotionDuration, ReasonCustomisableObj } from '@atlassian/cssxp-types-promotion';
import './select-promotion-form-field-v2.scss';
import { DISCOUNT_APPROVAL_PRECENT_THRESHOLD, MARKETPLACE_PROMO_CODE } from '@atlassian/cssxp-utils-entitlement';
import { DISCRETIONARY_DISCOUNT, isDiscretionaryDiscountPromotion, renderPromotionOption } from '../../helper';
import { validateApplicablePromocode } from './queries/validate-promo-code';
import { cloneDeep } from 'lodash';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_DISCOUNT_CYCLE_INTERVAL,
} from '@atlassian/cssxp-utils-generic';
import { useFeatureToggle } from '../../hooks';

export interface SelectPromotionSelectItemV2 {
  value: string | null; // Promotion Id
  promotion: SelectedPromotion;
  index: number;
}

export interface CustomPromotionProps {
  promotion: SelectedPromotion;
  index: number;
  onChange?: (value: string) => void;
  isDisabled?: boolean | undefined;
  customFieldNames?: string[];
}

const getActivePromotions = (promotions: any) => {
  return (promotions ?? [])?.filter((promotion: Promotion) => {
    return promotion.status === 'ACTIVE';
  });
};

const SelectPromotionSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,
  ...props
}: OptionProps<SelectPromotionSelectItemV2>) => {
  const [isEnabled] = useFeatureToggle();
  const { promotion } = data as { promotion: Promotion };
  const optionStyles = getStyles('option', props);
  const promotionTitle = promotion && promotion.title;
  const showCycleIntervalEnabled = isEnabled(ENABLE_DISCOUNT_CYCLE_INTERVAL);

  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`promotion-select-option-${promotion?.id}`}>
      <span data-testid={`promotion-select-option-${promotion?.id}`}>
        {!promotion?.id && !promotionTitle && (
          <>
            <strong>None</strong>
            <br />
          </>
        )}
        {renderPromotionOption(promotion, { showCycleInterval: showCycleIntervalEnabled })}
      </span>
    </section>
  );
};

export interface PromotionItemDetail {
  discountPercentage?: number;
  duration?: string;
  reason?: string;
  iterations?: number;
  promotionFieldName?: string;
}

export type SelectedPromotion = Promotion & {
  customFormField?: {
    duration?: Maybe<{
      value: string | undefined | null;
      label: string | undefined | null;
    }>;
    iterations?: Maybe<string>;
    value?: Maybe<string>;
    reason?: Maybe<{
      value: string | undefined | null;
      label: string | undefined | null;
    }>;
  };
};

interface Props {
  fieldName?: string;
  fieldNamePrefix?: string;
  fieldLabel?: string;
  singleFieldLabel?: string;
  isRequired?: boolean;
  availablePromotions?: Promotion[] | null;
  promotions?: SelectedPromotion[] | null;
  isDisabled?: boolean | false;
  includeAllPromotions?: boolean | false;
  availablePromotionTypes?: string[] | null | undefined;
  defaultValueRewindPane?: boolean | false;
  index?: number;
  onCustomValueChange?: (item: PromotionItemDetail) => void;
  canAddMultiplePromotions?: boolean | undefined;
  addAnotherPromotion?: () => void;
  onAfterAddPromotion?: (item: any) => void;
  onAfterRemovePromotion?: (values: any) => void;
  customFieldNames?: string[];
  entitlementId?: string;
  productId?: string | null;
  handlePromotionCodeError?: React.Dispatch<React.SetStateAction<boolean>>;
  onPromoCodeApplySuccess?: (item: any) => void;
}

export interface SelectCustomPromotionItemV2 {
  value: string;
  label: string;
}

// TODO: perhaps will remove these default values once customisable is available in promotion object.
const DefaultValueOption = {
  type: 'range',
  lowerBound: 10.0,
  upperBound: 100.0,
};

enum DefaultBoundvalue {
  LOWER_BOUND = 0,
  UPPER_BOUND = 100,
}

const DefaultDurationOption = ['ONCE', 'FOREVER', 'RECURRING'];

const DefaultIterationOption = {
  type: 'range',
  lowerBound: 1,
  upperBound: 3,
};

const formatReasonCode = (value: string) => value?.replace(/_/g, ' ')?.toUpperCase();

const hasIterationCheck = (value: string) => value === PromotionDuration.REPEATING;

const checkForDiscountApproval = (value: number) => value > DISCOUNT_APPROVAL_PRECENT_THRESHOLD;

const CustomPromotionField = ({ promotion, index, onChange, isDisabled, customFieldNames }: CustomPromotionProps) => {
  const firstBenefitItem = promotion?.benefits?.[0];

  const defaultSelectedReason: SelectCustomPromotionItemV2 = {
    value: (promotion?.customFormField?.reason?.value || promotion?.applicationReason?.id) ?? '',
    label: formatReasonCode(
      (promotion?.customFormField?.reason?.label || promotion?.applicationReason?.readableName) ?? ''
    ),
  };

  const defaultSelectedDuration: SelectCustomPromotionItemV2 = {
    value: (promotion?.customFormField?.duration?.value || firstBenefitItem?.duration) ?? '',
    label: capitalize((promotion?.customFormField?.duration?.label || firstBenefitItem?.duration) ?? ''),
  };

  const defaultSelectedValue = (promotion?.customFormField?.value || firstBenefitItem?.value) ?? '0';

  const defaultSelectedIterations = (promotion?.customFormField?.iterations || firstBenefitItem?.iterations) ?? '1';

  const initialIterationsFieldState = hasIterationCheck(defaultSelectedDuration?.value ?? '');

  const [isIterationsFieldVisible, setIsIterationsFieldVisible] = React.useState<boolean>(initialIterationsFieldState);

  React.useEffect(() => {
    // Make sure that correct value is set for initial and subsequent renderings
    setIsIterationsFieldVisible(initialIterationsFieldState);
  }, [initialIterationsFieldState]);

  const customPromotionOptions = (defaultTo(promotion?.customisableReasons, []) as ReasonCustomisableObj[])?.map(
    (reasonCodes: ReasonCustomisableObj) => {
      return {
        value: reasonCodes?.id,
        label: formatReasonCode(reasonCodes?.readableName),
      };
    }
  );

  const isCustomReasonCodeEnabled = promotion?.customisableReasons;

  const customDurationOptions = get(firstBenefitItem, 'customisable.duration', DefaultDurationOption)?.map(
    (duration: string) => ({
      value: duration,
      label: capitalize(duration ?? ''),
    })
  );

  const customValueOptions = firstBenefitItem?.customisable?.value ?? DefaultValueOption;
  const customIterationOptions = firstBenefitItem?.customisable?.iterations ?? DefaultIterationOption;

  const validator = (value: any, validatorRef: BenefitCustomisableValue) => {
    if (!Number.isInteger(toNumber(value))) {
      return 'Must be a number without decimal point';
    }
    if (
      toNumber(value) < toNumber(validatorRef?.lowerBound ?? DefaultBoundvalue.LOWER_BOUND) ||
      toNumber(value) > toNumber(validatorRef?.upperBound ?? DefaultBoundvalue.UPPER_BOUND)
    ) {
      return `Value cannot be less than ${validatorRef?.lowerBound ?? DefaultBoundvalue.LOWER_BOUND} and greater than ${
        validatorRef?.upperBound ?? DefaultBoundvalue.UPPER_BOUND
      }`;
    }
    return undefined;
  };

  const isCustomValueEnabled = firstBenefitItem?.customisable?.value;
  const isCustomDurationEnabled = firstBenefitItem?.customisable?.duration;
  const isCustomIterationEnabled = firstBenefitItem?.customisable?.iterations;
  const isCustomFieldNameProvided = customFieldNames?.length === 5;

  return promotion?.isPromotionCustomisable ? (
    <div data-testid={'custom-promotion-form'} className="custom-promotion-form">
      <Field<ValueType<boolean>> name={'customisable'} label={''} defaultValue={true}>
        {() => (
          <>
            {isCustomValueEnabled && (
              <div className="mb-32">
                <Field
                  name={isCustomFieldNameProvided ? `${customFieldNames?.[0]}.${index}` : `customValue.${index}`}
                  label="Value (%)"
                  isRequired={true}
                  validate={(value: any) => validator(value, customValueOptions)}
                  defaultValue={defaultSelectedValue}
                  isDisabled={isDisabled}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="customValue"
                        onChange={(e: any) => {
                          fieldProps.onChange(e.target.value);
                          if (onChange) {
                            onChange(e.target.value);
                          }
                        }}
                      />
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                    </>
                  )}
                </Field>
              </div>
            )}
            {isCustomDurationEnabled && (
              <div className="mb-32">
                <Field<ValueType<SelectCustomPromotionItemV2>, HTMLSelectElement>
                  name={isCustomFieldNameProvided ? `${customFieldNames?.[1]}.${index}` : `customDuration.${index}`}
                  label={'Duration'}
                  isRequired={true}
                  defaultValue={defaultSelectedDuration}
                  isDisabled={isDisabled}
                >
                  {({ fieldProps }) => (
                    <Select<SelectCustomPromotionItemV2>
                      {...fieldProps}
                      options={customDurationOptions}
                      isSearchable={false}
                      data-testid="custom-promotion-duration-drop-down"
                      // @ts-ignore
                      onChange={(selectedItem: SelectCustomPromotionItemV2) => {
                        const hasIterations = hasIterationCheck(selectedItem.value);
                        setIsIterationsFieldVisible(hasIterations);
                        fieldProps.onChange(selectedItem);
                      }}
                    />
                  )}
                </Field>
              </div>
            )}
            {isCustomIterationEnabled && isIterationsFieldVisible && (
              <div className="mb-32">
                <Field
                  name={isCustomFieldNameProvided ? `${customFieldNames?.[2]}.${index}` : `customIterations.${index}`}
                  label={'Billing cycles'}
                  isRequired={true}
                  validate={(value: any) => validator(value, customIterationOptions)}
                  defaultValue={defaultSelectedIterations}
                  isDisabled={isDisabled}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="customIterations"
                        data-testid="custom-promotion-iteration-text-field"
                      />
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                    </>
                  )}
                </Field>
              </div>
            )}
            {isCustomReasonCodeEnabled && (
              <div className="mb-32">
                <Field<ValueType<SelectCustomPromotionItemV2>, HTMLSelectElement>
                  name={isCustomFieldNameProvided ? `${customFieldNames?.[3]}.${index}` : `customReason.${index}`}
                  label={'Reason code'}
                  isRequired={true}
                  defaultValue={defaultSelectedReason}
                  isDisabled={isDisabled}
                >
                  {({ fieldProps }) => (
                    <Select<SelectCustomPromotionItemV2>
                      {...fieldProps}
                      options={customPromotionOptions}
                      isSearchable={false}
                      data-testid="custom-promotion-reason-drop-down"
                    />
                  )}
                </Field>
              </div>
            )}
          </>
        )}
      </Field>
    </div>
  ) : null;
};

interface PromotionFieldProps {
  fieldName: string;
  fieldLabel: string;
  isRequired?: boolean;
  isDisabled?: boolean | false;
  normalPromotions?: SelectPromotionSelectItemV2[];
  customPromotions?: SelectPromotionSelectItemV2[];
  index: number;
  removePromotion: (index: number) => void;
  updateFormValues: (formValue: SelectPromotionSelectItemV2, index: number) => void;
  isRemoveVisible: boolean;
  value: SelectPromotionSelectItemV2 | null | undefined;
  onCustomValueChange?: (customPromotionDetails: PromotionItemDetail, item?: any) => void;
  onAfterAddPromotion?: (item: any) => void;
  onAfterRemovePromotion?: (values: any) => void;
  customFieldNames?: string[];
}

const EmptyPromotionItem = {
  promotion: {
    id: '',
    type: '',
    title: '',
  },
  index: 0,
} as SelectPromotionSelectItemV2;

const DiscretionaryPromotionItem = {
  value: '',
  promotion: {
    id: '',
    title: 'Discretionary discount',
    type: DISCRETIONARY_DISCOUNT,
  },
  index: 1,
};

const PromotionField = ({
  fieldName = '',
  fieldLabel,
  isRequired,
  isDisabled,
  normalPromotions,
  customPromotions,
  index = 0,
  removePromotion,
  updateFormValues,
  isRemoveVisible = false,
  value,
  onCustomValueChange,
  onAfterAddPromotion,
  customFieldNames,
}: PromotionFieldProps) => {
  const isCustomPromotionDropdownVisible = isDiscretionaryDiscountPromotion(value);
  // customFieldNames - used in some scenarios of quotes
  const isCustomFieldNameProvided = customFieldNames?.length === 5;
  const defaultValueCustomPromotionField =
    isCustomPromotionDropdownVisible && value?.value
      ? {
          promotion: value?.promotion as Promotion,
          value: value?.value as string,
          index: value?.index as number,
        }
      : null;

  return (
    <div className="select-promotion-field">
      <Field<ValueType<SelectPromotionSelectItemV2>, HTMLSelectElement>
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
        isDisabled={isDisabled}
        defaultValue={isCustomPromotionDropdownVisible ? DiscretionaryPromotionItem : value}
      >
        {({ fieldProps }) => (
          <div data-testid="select-promotion-dropdown" className="select-promotion-dropdown-container mb-32">
            <Select<SelectPromotionSelectItemV2>
              {...fieldProps}
              options={normalPromotions}
              className="select-promotion-dropdown"
              classNamePrefix="select-promotion-dropdown"
              components={{
                Option: SelectPromotionSelectOption,
                //@ts-ignore
                SingleValue: SelectPromotionSelectOption,
              }}
              isSearchable={false}
              onChange={(itemValue) => {
                fieldProps.onChange(itemValue);
                itemValue && updateFormValues(itemValue as SelectPromotionSelectItemV2, index);
                onAfterAddPromotion && onAfterAddPromotion(itemValue);
              }}
            />
            {isRemoveVisible && !isDisabled && (
              <div className="remove-promotion-button">
                <Button
                  appearance="link"
                  testId={`remove-promotion-${index}`}
                  onClick={() => removePromotion(index)}
                  isDisabled={isDisabled}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>
        )}
      </Field>
      {isCustomPromotionDropdownVisible && (
        <Field<ValueType<SelectPromotionSelectItemV2>, HTMLSelectElement>
          name={isCustomFieldNameProvided ? `${customFieldNames?.[4]}-${index}` : `customPromotionId-${index}`}
          label="Custom discount"
          isRequired={true}
          isDisabled={isDisabled}
          defaultValue={defaultValueCustomPromotionField}
          validate={(value: any) => (value ? undefined : 'INVALID')}
        >
          {({ fieldProps, error }) => (
            <>
              <div data-testid="select-custom-promotion-dropdown">
                <Select<SelectPromotionSelectItemV2>
                  {...fieldProps}
                  options={customPromotions}
                  className="select-custom-promotion-dropdown mb-32"
                  classNamePrefix="select-custom-promotion-dropdown"
                  components={{
                    Option: SelectPromotionSelectOption,
                    //@ts-ignore
                    SingleValue: SelectPromotionSelectOption,
                  }}
                  isSearchable={false}
                  onChange={(item) => {
                    fieldProps.onChange(item);
                    if (onCustomValueChange) {
                      const value = get(item, 'promotion.benefits[0].value', '');
                      const duration = get(item, 'promotion.benefits[0].duration', '');
                      const reason = get(item, 'promotion.applicationReason.id', '');
                      const iterations = get(item, 'promotion.benefits[0].iterations', '');
                      onCustomValueChange(
                        {
                          discountPercentage: parseInt(value),
                          duration: duration,
                          reason: reason,
                          iterations: parseInt(iterations),
                        },
                        item
                      );
                    }
                  }}
                />
                {error && <ErrorMessage>This field is required</ErrorMessage>}
              </div>
              {
                <CustomPromotionField
                  {...(fieldProps.value as SelectPromotionSelectItemV2)}
                  onChange={(value) => {
                    if (onCustomValueChange) {
                      onCustomValueChange(
                        {
                          discountPercentage: parseInt(value),
                        },
                        fieldProps.value
                      );
                    }
                  }}
                  index={index}
                  isDisabled={fieldProps?.isDisabled}
                  customFieldNames={customFieldNames}
                />
              }
            </>
          )}
        </Field>
      )}
    </div>
  );
};

export const SelectPromotionFormFieldV2 = ({
  fieldName = 'promotionId',
  fieldNamePrefix = '',
  fieldLabel = 'Select discount',
  singleFieldLabel,
  isRequired = false,
  availablePromotions = [],
  isDisabled,
  promotions = [],
  includeAllPromotions = false,
  defaultValueRewindPane = false,
  canAddMultiplePromotions = true,
  availablePromotionTypes = [],
  onCustomValueChange,
  onAfterAddPromotion,
  onAfterRemovePromotion,
  customFieldNames,
  entitlementId = '',
  productId = '',
  handlePromotionCodeError,
  onPromoCodeApplySuccess,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const [validatePromocode, setValidatePromocode] = useState(false);
  const [formValues, setFormValues] = useState<any>(promotions?.length ? promotions : [{}]);
  const activeAvailablePromotions = getActivePromotions(availablePromotions);
  const [promocode, setPromocode] = useState('');
  const [promocodeAPIError, setPromocodeAPIError] = useState('');
  const [promocodeNotApplicableErrors, setPromocodeNotApplicableErrors] = useState(null);
  const isPromocodeNotApplied =
    formValues.find(
      (item: any) =>
        isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) &&
        item?.promotion?.type === MARKETPLACE_PROMO_CODE &&
        item?.value === undefined
    ) &&
    !validatePromocode &&
    promocode;

  const { data, loading, error } = useQuery(validateApplicablePromocode, {
    variables: {
      id: entitlementId,
      promoCodes: [
        {
          code: promocode,
          productKey: productId,
        },
      ],
    },
    skip: !validatePromocode,
    // Removed policy, as it was causing reload of page, for some cases
    // fetchPolicy: 'cache-and-network',
    // nextFetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    const activePromotions = includeAllPromotions ? availablePromotions : activeAvailablePromotions;
    const hasPromotion = activePromotions.length !== 0;

    const defaultValues = [];
    if (promotions && promotions?.length > 0) {
      for (let index = 0; index < promotions.length; index++) {
        defaultValues.push(
          hasPromotion
            ? promotions && promotions.length > 0
              ? {
                  promotion: promotions[index],
                  value: promotions[index]?.id || '',
                  customReason: {
                    value: promotions[index]?.applicationReason?.id || '',
                    label: promotions[index]?.applicationReason?.readableName || '',
                  },
                  customValue: promotions[index]?.benefits?.[index]?.value || 0,
                  customIterations: promotions[index]?.benefits?.[index]?.iterations || 0,
                  customDuration: {
                    value: promotions[index]?.benefits?.[index]?.duration || '',
                    label: promotions[index]?.benefits?.[index]?.duration || '',
                  },
                  customisable: promotions[index]?.isPromotionCustomisable ?? false,
                  isIterationFieldEnabled:
                    promotions[index]?.benefits?.[index]?.duration === PromotionDuration.REPEATING,
                  label: promotions[index]?.title,
                  index: index,
                }
              : defaultValueRewindPane
              ? EmptyPromotionItem
              : null
            : defaultValueRewindPane
            ? EmptyPromotionItem
            : undefined
        );
      }

      setFormValues([...defaultValues]);
    }
  }, []);

  React.useEffect(() => {
    if (validatePromocode && !loading) {
      let isError;
      handlePromotionCodeError && handlePromotionCodeError(false);
      if (error) {
        setPromocodeAPIError(error?.toString());
        isError = true;
      } else if (data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason) {
        setPromocodeNotApplicableErrors(
          data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason?.join('.')
        );
        isError = true;
        handlePromotionCodeError && handlePromotionCodeError(true);
      }

      const index = formValues.findIndex((item: any) => item?.promotion?.type === MARKETPLACE_PROMO_CODE);
      const updatedFormValues = cloneDeep(formValues);

      let promoCodeDetails;
      if (index !== -1 && !isError) {
        promoCodeDetails = data?.entitlementDetails?.promoCodesApplicable?.promotions?.[0];
        updatedFormValues[index].promotion = {
          ...promoCodeDetails,
          type: MARKETPLACE_PROMO_CODE,
        };
        updatedFormValues[index].value = data?.entitlementDetails?.promoCodesApplicable?.promotionId;
      } else {
        updatedFormValues[index].value = null;
      }

      setFormValues(updatedFormValues);
      setValidatePromocode(false);
      onPromoCodeApplySuccess && onPromoCodeApplySuccess(promoCodeDetails);
    }
  }, [validatePromocode, loading, onPromoCodeApplySuccess]);

  if (!activeAvailablePromotions || activeAvailablePromotions.length === 0) {
    return null;
  }
  const updateFormValues = (formValue: SelectPromotionSelectItemV2, currentIndex: number) => {
    setFormValues((formValues: SelectPromotionSelectItemV2[]) => {
      const updatedFormValues = [...formValues];
      updatedFormValues[currentIndex] = formValue;

      return updatedFormValues;
    });
  };

  const addAnotherPromotion = () => {
    setFormValues([...formValues, {}]);
  };

  const removePromotion = async (i: number) => {
    const newFromValues = formValues.filter((_formValue: any, index: number) => i !== index);
    setFormValues(newFromValues);
    if (onAfterRemovePromotion) {
      onAfterRemovePromotion(newFromValues);
    }
  };

  const filterFromSelectedValues = (options: SelectPromotionSelectItemV2[], currentIndex: number) => {
    const updatedFormValues = cloneDeep(formValues);
    updatedFormValues.splice(currentIndex, 1);

    const filteredOptions = options.filter((obj: any) => {
      if (availablePromotionTypes?.includes(obj?.promotion?.type)) {
        return false;
      }

      const isValuePresent = updatedFormValues?.some((item: { promotion: { type: Maybe<string> | undefined } }) => {
        return item?.promotion?.type === obj?.promotion?.type;
      });
      return !isValuePresent;
    });
    return filteredOptions;
  };

  const selectItems = activeAvailablePromotions.map((promotion: Promotion) => {
    return {
      promotion,
      value: promotion.id,
    } as SelectPromotionSelectItemV2;
  });
  const customPromotions: SelectPromotionSelectItemV2[] = selectItems.filter(
    (item: SelectPromotionSelectItemV2) => item.promotion?.isPromotionCustomisable === true
  );
  const normalPromotions: SelectPromotionSelectItemV2[] = selectItems.filter(
    (item: SelectPromotionSelectItemV2) => item.promotion?.isPromotionCustomisable !== true
  );
  normalPromotions.unshift(DiscretionaryPromotionItem);
  normalPromotions.unshift(EmptyPromotionItem);

  const handleAddPromotion = () => {
    if (
      Object.entries(formValues[0]).length < 1 || // No promotion selected
      (Object.entries(formValues[0]).length > 0 && !formValues[0]?.promotion?.title) || // 'None' selected
      formValues.length === 5 ||
      Object.entries(formValues[formValues.length - 1]).length < 1 || // last promotion input has no selection
      (Object.entries(formValues[formValues.length - 1]).length > 0 && !formValues[0]?.promotion?.title) // last promotion input has a selection of 'None'
    ) {
      // max statcking === 5
      return false;
    }

    return true;
  };

  return (
    <div className="promotion-field-conatiner">
      {formValues?.map((formValue: SelectPromotionSelectItemV2, index: number) => {
        const optionValue = formValue?.value;
        const hasSelectedOptions = optionValue || optionValue === '';
        const key = hasSelectedOptions ? `${optionValue}-${index}` : index;

        return (
          <>
            <PromotionField
              key={key}
              fieldName={`${fieldNamePrefix}${fieldName}-${index}`}
              fieldLabel={
                index === 0 ? singleFieldLabel || fieldLabel : singleFieldLabel ? '' : `${fieldLabel} ${index + 1}`
              }
              isRequired={isRequired}
              isDisabled={isDisabled}
              normalPromotions={filterFromSelectedValues(normalPromotions, index)}
              customPromotions={customPromotions}
              removePromotion={removePromotion}
              updateFormValues={updateFormValues}
              index={index}
              isRemoveVisible={formValues.length > 1}
              value={hasSelectedOptions ? formValue : null}
              onCustomValueChange={onCustomValueChange}
              onAfterAddPromotion={onAfterAddPromotion}
              customFieldNames={customFieldNames}
            />
            {isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) &&
              formValues?.[index]?.promotion?.type === MARKETPLACE_PROMO_CODE && (
                <FormSection>
                  <Field
                    label={'Promo code'}
                    name={`${fieldNamePrefix}promoCode`}
                    isRequired={true}
                    validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                  >
                    {({ fieldProps, error }) => (
                      <div className="promo-code-container">
                        <div className="promo-code-input">
                          <Textfield
                            {...fieldProps}
                            name={`${fieldNamePrefix}promoCode`}
                            testId="promo-code"
                            placeholder={'Enter Promo code'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const index = formValues.findIndex(
                                (item: any) => item.promotion?.type === MARKETPLACE_PROMO_CODE
                              );
                              const updatedFormValues = cloneDeep(formValues);
                              updatedFormValues[index].promotion = {
                                type: MARKETPLACE_PROMO_CODE,
                                title: 'Promo code',
                                status: 'ACTIVE',
                              };
                              updatedFormValues[index].value = undefined;
                              setFormValues(updatedFormValues);
                              setPromocode(e.target.value);
                              fieldProps.onChange(e.target.value);

                              setPromocodeNotApplicableErrors(null);
                              setPromocodeAPIError('');
                            }}
                            isDisabled={loading}
                          />
                          <LoadingButton
                            appearance="primary"
                            isSelected
                            isLoading={loading}
                            isDisabled={loading || !promocode}
                            testId="apply-promocode"
                            aria-label="Apply promocode"
                            onClick={() => {
                              setPromocodeNotApplicableErrors(null);
                              setPromocodeAPIError('');
                              setValidatePromocode(true);
                            }}
                          >
                            Apply
                          </LoadingButton>
                        </div>
                        {error && !promocode && <ErrorMessage>This field is required</ErrorMessage>}
                        {isPromocodeNotApplied && <ErrorMessage>Please apply the promocode.</ErrorMessage>}
                        {promocodeNotApplicableErrors && <ErrorMessage>{promocodeNotApplicableErrors}</ErrorMessage>}
                        {promocodeAPIError && <ErrorMessage>{promocodeAPIError}</ErrorMessage>}
                      </div>
                    )}
                  </Field>
                  {formValues?.[index]?.promotion?.benefits && (
                    <div className="promocode-promotion-details">
                      {renderPromotionOption(formValues?.[index]?.promotion)}
                    </div>
                  )}
                </FormSection>
              )}
          </>
        );
      })}
      {canAddMultiplePromotions && handleAddPromotion() && (
        <Button
          iconBefore={<AddIcon label="Add another discount" size="small" />}
          appearance="link"
          testId="add-another-promotion"
          onClick={() => addAnotherPromotion && addAnotherPromotion()}
          isDisabled={isDisabled}
        >
          Add another discount
        </Button>
      )}
    </div>
  );
};
